import { createContext, useContext, useCallback, useMemo, useReducer } from 'react'
import { processImage } from '../lib/image'
import { uploadScanPhoto, extractDataPlate, getEstimate, createBooking } from '../lib/api'

const EstimateContext = createContext(null)

// Flow order: symptom → photo → extracting → verdict → booking → confirmation

const initialState = {
  step: 'symptom',
  symptom: null,
  entryMode: 'photo', // 'photo' | 'manual'
  photo: null, // { previewUrl, storagePath }
  unit: null,
  estimate: null,
  chosenOptionType: null,
  booking: null,
  confirmation: null,
  busy: false,
  busyMessage: '',
  error: null,
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SYMPTOM':
      return { ...state, symptom: action.symptom, step: 'photo', entryMode: 'photo', error: null }
    case 'SET_ENTRY_MODE':
      return { ...state, entryMode: action.mode, error: null }
    case 'GO':
      return { ...state, step: action.step, error: null }
    case 'BUSY':
      return { ...state, busy: true, busyMessage: action.message || '', error: null }
    case 'IDLE':
      return { ...state, busy: false, busyMessage: '' }
    case 'ERROR':
      return { ...state, busy: false, busyMessage: '', error: action.error }
    case 'SET_PHOTO':
      return { ...state, photo: action.photo }
    case 'EXTRACTED':
      return { ...state, unit: action.unit }
    case 'ESTIMATED':
      return {
        ...state,
        estimate: action.estimate,
        chosenOptionType: action.estimate?.primaryType || null,
        step: 'verdict',
        busy: false,
        busyMessage: '',
      }
    case 'CHOOSE_OPTION':
      return { ...state, chosenOptionType: action.optionType, step: 'booking', error: null }
    case 'BOOKED':
      return {
        ...state,
        confirmation: action.confirmation,
        booking: action.booking,
        step: 'confirmation',
        busy: false,
        busyMessage: '',
      }
    case 'RESET':
      return { ...initialState }
    default:
      return state
  }
}

export function EstimateProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setSymptom = useCallback((symptom) => dispatch({ type: 'SET_SYMPTOM', symptom }), [])
  const setEntryMode = useCallback((mode) => dispatch({ type: 'SET_ENTRY_MODE', mode }), [])
  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])

  const goBack = useCallback(() => {
    dispatch({ type: 'GO', step: prevStep(state) })
  }, [state])

  // Photo path: compress → upload for the record → Gemini vision extract →
  // server estimate → verdict.
  const submitPhoto = useCallback(
    async (file) => {
      try {
        dispatch({ type: 'GO', step: 'extracting' })
        dispatch({ type: 'BUSY', message: 'Reading the data plate…' })
        const { blob, base64, previewUrl } = await processImage(file)

        // Upload for the permanent record (non-blocking to the vision call in
        // spirit; we await so the storagePath is known for booking).
        const { storagePath } = await uploadScanPhoto(blob)
        dispatch({ type: 'SET_PHOTO', photo: { previewUrl, storagePath } })

        const unit = await extractDataPlate({ imageBase64: base64, symptom: state.symptom })
        dispatch({ type: 'EXTRACTED', unit })

        dispatch({ type: 'BUSY', message: 'Pricing your options…' })
        const estimate = await getEstimate({ unit, symptom: state.symptom })
        dispatch({ type: 'ESTIMATED', estimate })
      } catch (err) {
        console.error('submitPhoto failed', err)
        dispatch({
          type: 'ERROR',
          error:
            "We couldn't read that photo automatically. Try again with the sticker in good light — or enter the details by hand.",
        })
        dispatch({ type: 'GO', step: 'photo' })
        dispatch({ type: 'SET_ENTRY_MODE', mode: 'manual' })
      }
    },
    [state.symptom],
  )

  // Manual path: user typed brand/gallons/fuel/age → server estimate → verdict.
  const submitManual = useCallback(
    async (fields) => {
      try {
        dispatch({ type: 'GO', step: 'extracting' })
        dispatch({ type: 'BUSY', message: 'Pricing your options…' })
        const year = fields.approxAge != null ? 2026 - Number(fields.approxAge) : null
        const unit = {
          brand: fields.brand || null,
          model: null,
          serial: null,
          gallons: fields.gallons != null ? Number(fields.gallons) : null,
          fuelType: fields.fuelType || 'unknown',
          manufactureYear: year,
          manufactureMonth: null,
          manufactureDate: year ? String(year) : null,
          ageYears: fields.approxAge != null ? Number(fields.approxAge) : null,
          decode: { confidence: 'manual', decoded: false, source: 'manual entry' },
          extractionConfidence: 'manual',
          needsConfirmation: false,
          enteredManually: true,
        }
        dispatch({ type: 'EXTRACTED', unit })
        const estimate = await getEstimate({ unit, symptom: state.symptom })
        dispatch({ type: 'ESTIMATED', estimate })
      } catch (err) {
        console.error('submitManual failed', err)
        dispatch({ type: 'ERROR', error: 'Something went wrong pricing that. Please try again.' })
        dispatch({ type: 'GO', step: 'photo' })
      }
    },
    [state.symptom],
  )

  const chooseOption = useCallback(
    (optionType) => dispatch({ type: 'CHOOSE_OPTION', optionType }),
    [],
  )

  const submitBooking = useCallback(
    async (bookingFields) => {
      try {
        dispatch({ type: 'BUSY', message: 'Booking your appointment…' })
        const payload = {
          symptom: state.symptom,
          unit: state.unit,
          photoStoragePath: state.photo?.storagePath || null,
          chosenOptionType: state.chosenOptionType,
          customer: {
            name: bookingFields.name,
            phone: bookingFields.phone,
            email: bookingFields.email || null,
            address: bookingFields.address,
          },
          timeWindow: bookingFields.timeWindow,
          notes: bookingFields.notes || null,
        }
        const confirmation = await createBooking(payload)
        dispatch({ type: 'BOOKED', confirmation, booking: bookingFields })
      } catch (err) {
        console.error('submitBooking failed', err)
        dispatch({
          type: 'ERROR',
          error: "We couldn't complete the booking. Please try again, or call us and we'll set it up.",
        })
      }
    },
    [state.symptom, state.unit, state.photo, state.chosenOptionType],
  )

  const value = useMemo(
    () => ({
      ...state,
      setSymptom,
      setEntryMode,
      submitPhoto,
      submitManual,
      chooseOption,
      submitBooking,
      goBack,
      reset,
    }),
    [state, setSymptom, setEntryMode, submitPhoto, submitManual, chooseOption, submitBooking, goBack, reset],
  )

  return <EstimateContext.Provider value={value}>{children}</EstimateContext.Provider>
}

function prevStep(state) {
  switch (state.step) {
    case 'photo':
      return 'symptom'
    case 'verdict':
      return 'photo'
    case 'booking':
      return 'verdict'
    default:
      return 'symptom'
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEstimate() {
  const ctx = useContext(EstimateContext)
  if (!ctx) throw new Error('useEstimate must be used within EstimateProvider')
  return ctx
}
